import React, { useState, useRef, useEffect } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';

export function Slider({ 
    min = 0, 
    max = 100, 
    step = 1, 
    value = 50, 
    onChange,
    className = ''
}) {
    const [isDragging, setIsDragging] = useState(false);
    const trackRef = useRef(null);
    const [localValue, setLocalValue] = useState(value);
    
    // Update local state when prop changes
    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    const percentage = ((localValue - min) / (max - min)) * 100;

    const handlePointerDown = (e) => {
        setIsDragging(true);
        updateValue(e.clientX);
    };

    const handlePointerMove = (e) => {
        if (!isDragging) return;
        updateValue(e.clientX);
    };

    const handlePointerUp = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('pointermove', handlePointerMove);
            window.addEventListener('pointerup', handlePointerUp);
        } else {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        }
        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        };
    }, [isDragging]);

    const updateValue = (clientX) => {
        if (!trackRef.current) return;
        const rect = trackRef.current.getBoundingClientRect();
        let percent = (clientX - rect.left) / rect.width;
        percent = Math.max(0, Math.min(1, percent));
        
        let newValue = min + percent * (max - min);
        newValue = Math.round(newValue / step) * step;
        
        if (newValue !== localValue) {
            setLocalValue(newValue);
            onChange?.(newValue);
        }
    };

    return (
        <div 
            className={`relative w-full h-8 flex items-center cursor-pointer ${className}`}
            onPointerDown={handlePointerDown}
        >
            {/* Track Background */}
            <div 
                ref={trackRef}
                className="w-full h-1.5 bg-[var(--bg-muted)] rounded-full relative overflow-hidden"
            >
                {/* Active Fill */}
                <motion.div 
                    className="absolute top-0 left-0 h-full bg-[var(--accent)] origin-left"
                    style={{ width: `${percentage}%` }}
                    transition={{ type: 'spring', stiffness: 500, damping: 40 }}
                />
            </div>
            
            {/* Thumb */}
            <motion.div
                className="absolute top-1/2 -mt-2.5 w-5 h-5 bg-white border border-[var(--border)] shadow-md rounded-full"
                style={{ left: `calc(${percentage}% - 10px)` }}
                animate={{ scale: isDragging ? 1.2 : 1 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            />
        </div>
    );
}
